import { tv } from 'tailwind-variants';

import { createEnumVariants } from '../../../utils/commonTailwindVariants.utils';

import { GiantButtonSize, GiantButtonType } from './GiantButton.consts';

import {
  Icon,
  Icons,
  LoadingSpinner,
  LoadingSpinnerSize,
  Size,
  TextIconColor,
  ToolTip,
  Typography,
  TypographyAlignment,
  TypographyCasing,
  TypographyFont,
  TypographySize
} from '~/components/core';

// Define tv component for complex variant combinations
const giantButtonStyles = tv({
  base: 'flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-lg px-2 transition-all duration-100',
  variants: {
    size: createEnumVariants<typeof GiantButtonSize>({
      [GiantButtonSize.DEFAULT]: 'h-16',
      [GiantButtonSize.LARGE]: 'h-20'
    }),
    buttonType: createEnumVariants<typeof GiantButtonType>({
      [GiantButtonType.OUTLINED_COLOR]:
        'border-primary-bright hover:border-primary-highlight bg-black-overlay border shadow-[inset_0px_0px_40px_0px_rgba(32,0,200,0.30)] hover:shadow-[inset_0px_0px_40px_0px_#2000C8]',
      [GiantButtonType.OUTLINED]:
        'border border-white/10 bg-transparent enabled:hover:border-white/20',
      [GiantButtonType.SOLID]: 'bg-primary-dull/10 hover:bg-primary-dull/20'
    }),
    disabled: {
      true: 'bg-black-overlay cursor-not-allowed rounded-lg border-[1.25px] border-dashed border-white/20 opacity-50'
    }
  }
});

interface GiantButtonProps {
  label: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  type: GiantButtonType;
  disabled?: boolean;
  endIcon?: Icon | undefined;
  loading?: boolean;
  size?: GiantButtonSize;
  startIcon?: Icon | undefined;
  tooltip?: string;
}

const GiantButton: React.FC<GiantButtonProps> = ({
  onClick,
  label,
  type,
  disabled = false,
  tooltip,
  endIcon,
  startIcon,
  loading = false,
  size = GiantButtonSize.DEFAULT
}) => {
  const isDisabled = disabled || loading;

  const buttonClasses = giantButtonStyles({
    size,
    buttonType: type,
    disabled: isDisabled
  });

  return (
    <ToolTip content={tooltip ?? ''} offset={[0, 8]}>
      {/* Wrap tooltip in span so it works while button is disabled */}
      <span>
        <button
          className={buttonClasses}
          data-testid="giant-button"
          disabled={isDisabled}
          id="giant-button"
          onClick={isDisabled ? undefined : onClick}
        >
          {!loading && (
            <>
              <div
                className="flex items-center justify-center gap-1"
                id="start-icon-text-container"
              >
                {startIcon && (
                  <div className="ml-1">
                    <Icons
                      color={TextIconColor.PRIMARY}
                      icon={startIcon}
                      size={Size.SMALL}
                    />
                  </div>
                )}
                <Typography
                  alignment={TypographyAlignment.CENTER}
                  casing={TypographyCasing.UPPERCASE}
                  color={TextIconColor.PRIMARY}
                  font={TypographyFont.INTERACTIVE}
                  size={TypographySize.SMALL}
                >
                  {label}
                </Typography>
              </div>
              {endIcon && (
                <div className="ml-1">
                  <Icons color={TextIconColor.PRIMARY} icon={endIcon} />
                </div>
              )}
            </>
          )}
          {loading && <LoadingSpinner size={LoadingSpinnerSize.SMALL} />}
        </button>
      </span>
    </ToolTip>
  );
};

export default GiantButton;
